import { useState, useEffect, useCallback } from "react";
import {
  getUserFromStorage,
  clearUserSession,
  getUserPermissions,
  hasPermission as rbacHasPermission,
  hasAnyPermission as rbacHasAnyPermission,
  canPerformAction as rbacCanPerformAction,
  canManageRole as rbacCanManageRole,
  canAccessPage as rbacCanAccessPage,
  getNavigation as rbacGetNavigation,
  getStoredPermissions,
} from "../utils/rbacConfig";

/**
 * usePermissions hook — Fix 1.16: Now reads stored permissions from localStorage
 * for sub-users and passes them to all permission check functions.
 */
const usePermissions = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isSubUser, setIsSubUser] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load user data on mount
  useEffect(() => {
    try {
      const userData = getUserFromStorage();

      if (userData) {
        setUser(userData);
        setUserRole(userData.role);
        setIsSubUser(userData.isSubUser || false);

        // Get effective permissions (uses stored permissions for sub-users)
        const isMainUser = !userData.isSubUser;
        const storedPerms = userData.isSubUser ? getStoredPermissions() : null;
        const permissions = getUserPermissions(userData.role, isMainUser, storedPerms);
        setUserPermissions(permissions);
      }
    } catch (error) {
      console.error('usePermissions: Error loading user:', error);
    }

    setLoading(false);
  }, []);

  // Get stored permissions for sub-users (memoized reference)
  const getPerms = useCallback(() => {
    return isSubUser ? getStoredPermissions() : null;
  }, [isSubUser]);

  // Check if user has permission
  const checkPermission = useCallback((permission) => {
    if (!userRole) return false;
    const isMainUser = !isSubUser;
    return rbacHasPermission(userRole, permission, isMainUser, getPerms());
  }, [userRole, isSubUser, getPerms]);

  // Check if user can perform action
  const checkAction = useCallback((action) => {
    if (!userRole) return false;
    const isMainUser = !isSubUser;
    return rbacCanPerformAction(userRole, action, isMainUser, getPerms());
  }, [userRole, isSubUser, getPerms]);

  // Check if user has any permission
  const checkAnyPermission = useCallback((permissions) => {
    if (!userRole) return false;
    const isMainUser = !isSubUser;
    return rbacHasAnyPermission(userRole, permissions, isMainUser, getPerms());
  }, [userRole, isSubUser, getPerms]);

  // Check page access
  const checkPageAccess = useCallback((pagePath) => {
    if (!userRole) return false;
    const isMainUser = !isSubUser;
    return rbacCanAccessPage(userRole, pagePath, isMainUser, getPerms());
  }, [userRole, isSubUser, getPerms]);

  // Check role management
  const checkRoleManagement = useCallback((targetRole) => {
    if (!userRole) return false;
    const isMainUser = !isSubUser;
    return rbacCanManageRole(userRole, targetRole, isMainUser);
  }, [userRole, isSubUser]);

  // Get navigation items
  const getNavigationItems = useCallback(() => {
    if (!userRole) return [];
    const isMainUser = !isSubUser;
    return rbacGetNavigation(userRole, isMainUser, getPerms());
  }, [userRole, isSubUser, getPerms]);

  // Logout
  const logout = useCallback(() => {
    clearUserSession();
    setUser(null);
    setUserRole(null);
    setIsSubUser(false);
    setUserPermissions([]);
    window.location.href = '#/';
  }, []);

  return {
    // User data
    user,
    userRole,
    isSubUser,
    userPermissions,
    loading,

    // Permission checking functions
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    canAccessPage: checkPageAccess,
    canPerformAction: checkAction,
    canManageRole: checkRoleManagement,

    // Navigation
    getNavigation: getNavigationItems,

    // Utility functions
    logout
  };
};

export default usePermissions;
